/**
 * WebSocket Client for TradeRewind Backend
 * Handles real-time data streaming
 */

import debug from '../utils/debug';

// Dynamically determine WebSocket URL based on environment
const getWebSocketUrl = (): string => {
  // If explicitly set in environment, use that
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }
  
  // Auto-detect based on current location
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    
    // If running on Vercel (or any production domain), use the backend URL
    if (host.includes('vercel.app') || host.includes('stockreplay') || host.includes('traderewind')) {
      // Backend is running behind Caddy reverse proxy with automatic HTTPS
      const backendUrl = import.meta.env.VITE_BACKEND_URL || '136.109.98.24.nip.io';
      
      // If user provided full WebSocket URL (wss:// or ws://), use it directly
      if (backendUrl.startsWith('wss://') || backendUrl.startsWith('ws://')) {
        return backendUrl;
      }
      
      // If user provided http/https URL, convert to WebSocket
      if (backendUrl.startsWith('https://')) {
        const backendHost = backendUrl.replace('https://', '');
        return `wss://${backendHost}`;
      } else if (backendUrl.startsWith('http://')) {
        const backendHost = backendUrl.replace('http://', '');
        return `ws://${backendHost}`;
      }
      
      // Assume it's a host, use wss://
      return `wss://${backendUrl}`;
    }
    
    // For localhost, use the configured port or default
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      const port = import.meta.env.VITE_API_PORT || '3000';
      return `ws://localhost:${port}`;
    }
    
    // Fallback: use same host as frontend
    return `${protocol}//${host}`;
  }
  
  // Server-side fallback
  return 'ws://localhost:3000';
};

const WS_URL = getWebSocketUrl();

// Log the WebSocket URL for debugging
console.log('[WebSocket] Connecting to:', WS_URL);

export enum WebSocketChannel {
  MARKET = 'candles',  // Changed from 'market' to match backend
  REPLAY = 'replay',
  NOTIFICATIONS = 'notifications',
}

interface WebSocketMessage {
  type: string;
  payload?: any;  // For legacy messages
  channel?: string;  // For update messages
  symbol?: string;
  interval?: string;
  data?: any;
  timestamp?: number;
}

type MessageHandler = (message: WebSocketMessage) => void;

class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map();
  private isConnecting = false;

  constructor(url: string) {
    this.url = url;
  }

  connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return Promise.resolve();
    }

    this.isConnecting = true;
    const wsUrl = `${this.url}/ws`;
    console.log('[WS] Attempting connection to:', wsUrl);

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('[WS] ✅ Connected to backend successfully');
          debug.info('[WS] Connected to backend');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('[WS] Error parsing message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('[WS] ❌ WebSocket connection error:', error);
          console.error('[WS] Failed to connect to:', wsUrl);
          console.error('[WS] This could be due to:');
          console.error('  1. Backend server is not running');
          console.error('  2. Backend does not support secure WebSocket (wss://)');
          console.error('  3. CORS/firewall blocking the connection');
          console.error('  4. SSL/TLS certificate issues');
          this.isConnecting = false;
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log('[WS] Connection closed:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
          debug.info('[WS] Disconnected from backend');
          this.isConnecting = false;
          this.attemptReconnect();
        };
      } catch (error) {
        console.error('[WS] Exception creating WebSocket:', error);
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WS] Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    debug.info(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      this.connect().catch((error) => {
        console.error('[WS] Reconnection failed:', error);
      });
    }, delay);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  subscribe(channel: WebSocketChannel, symbol: string, interval?: string) {
    debug.stream('[WS] Subscribing:', { channel, symbol, interval });
    this.send({
      type: 'subscribe',
      payload: { channel, symbol, interval },
    });
  }

  unsubscribe(channel: WebSocketChannel, symbol: string, interval?: string) {
    this.send({
      type: 'unsubscribe',
      payload: { channel, symbol, interval },
    });
  }

  on(messageType: string, handler: MessageHandler) {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set());
    }
    this.messageHandlers.get(messageType)!.add(handler);
  }

  off(messageType: string, handler: MessageHandler) {
    const handlers = this.messageHandlers.get(messageType);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  private handleMessage(message: WebSocketMessage) {
    debug.stream('[WS] Message received:', {
      type: message.type,
      channel: message.channel,
      symbol: message.symbol,
      interval: message.interval,
      hasData: !!message.data,
      registeredHandlers: Array.from(this.messageHandlers.keys())
    });

    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      debug.stream(`[WS] Dispatching to ${handlers.size} handler(s) for type '${message.type}'`);
      handlers.forEach((handler) => handler(message));
    } else {
      debug.stream(`[WS] No handlers registered for type '${message.type}'`);
    }

    // Also notify wildcard handlers
    const wildcardHandlers = this.messageHandlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => handler(message));
    }
  }

  private send(message: WebSocketMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      debug.stream('[WS] Sending message:', message);
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[WS] Cannot send message, not connected. ReadyState:', this.ws?.readyState);
    }
  }

  ping() {
    this.send({ type: 'ping', payload: {} });
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
export const wsClient = new WebSocketClient(WS_URL);

// Auto-connect on module load
wsClient.connect().catch((error) => {
  console.error('[WS] Initial connection failed:', error);
});

export default wsClient;
